/**
 * Read-only viewer for a grooming appointment's published report (owner side).
 * Fetches /user/bookings/:id/groom-report; shows "not published yet" when absent
 * (the backend only returns PUBLISHED reports — DRAFTs 404 → null). Shows the
 * services performed, coat-condition rating, notes, and before/after photos.
 */
import { useEffect } from 'react';
import { Clock, Loader2, Scissors, Star, X } from 'lucide-react';
import { ImageCarousel } from '@/components/image-carousel';
import { use_owner_groom_report } from '@/features/groom-report/hooks';

function fmt_time(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function GroomReportViewModal({ booking_id, on_close }: { booking_id: string; on_close: () => void }) {
  const report_q = use_owner_groom_report(booking_id);
  const report = report_q.data ?? null;

  useEffect(() => {
    const on_key = (e: KeyboardEvent) => e.key === 'Escape' && on_close();
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="groom-report-view-modal"
      onClick={on_close}
    >
      <div className="my-8 w-full max-w-lg rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
            <Scissors className="h-5 w-5 text-primary" /> Groom report
          </h2>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted" data-testid="groom-report-view-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {report_q.isLoading ? (
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !report ? (
          <p className="p-6 text-sm text-muted-foreground" data-testid="groom-report-view-empty">
            Your groomer hasn't published a report for this appointment yet.
          </p>
        ) : (
          <div className="flex flex-col gap-4 p-5 text-sm">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-muted-foreground" />
                {fmt_time(report.actual_start)} → {fmt_time(report.actual_end)}
              </span>
              {report.coat_rating != null && (
                <span className="flex items-center gap-1" data-testid="groom-report-coat-rating">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${i < report.coat_rating! ? 'fill-primary text-primary' : 'text-muted-foreground/40'}`} />
                  ))}
                </span>
              )}
            </div>

            {report.services_performed && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Services performed</p>
                <p className="whitespace-pre-wrap">{report.services_performed}</p>
              </div>
            )}

            {report.notes && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p>
                <p className="whitespace-pre-wrap">{report.notes}</p>
              </div>
            )}

            {report.before_photos.length > 0 && (
              <div className="flex flex-col gap-1.5" data-testid="groom-report-before-photos">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Before</p>
                <ImageCarousel images={report.before_photos} />
              </div>
            )}

            {report.after_photos.length > 0 && (
              <div className="flex flex-col gap-1.5" data-testid="groom-report-after-photos">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">After</p>
                <ImageCarousel images={report.after_photos} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
